import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Typography,
    TextField,
    Pagination,
    CircularProgress,
    Box
} from '@mui/material';
import { withStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Styles } from '../../../../../config/design/styles';
import {
    calcStockPrice,
    currencySymbol,
    price,
    scrollTop,
    showTableDate
} from '../../../../../config/values';
import {
    isAdmin,
    TablePage
} from '../../../../../config/Permissions';
import { defaultVariables } from '../../../../../config';
import AddStockButton from './AddStockButton';
import Grid from "@mui/material/Grid2";
import ItemsSkeleton from "../../../../components/loaders/ItemsSkeleton";
import RayaField from "../../../../components/elements/fields/RayaField";
import {ChevronLeft, CircleOff, Search} from "lucide-react";
import {theme} from "../../../../../config/design/theme";
import Button from "@mui/material/Button";
import {Link} from "react-router-dom";
import NothingFound from "../../../../components/elements/NothingFound";

const MyStocks = ({ classes }) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const [stocks, setStocks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [perPage] = useState(TablePage);

    const fetchStocks = () => {
        setLoading(true);
        axios
            .get(`${defaultVariables.url}/stocks/quickView?page=${page}&per_page=${perPage}&search=${search}`, {
                headers: {
                    Accept: 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('ACT')}`,
                },
            })
            .then((res) => {
                const mapped = res.data.data.map((row) => ({
                    id: row.id,
                    user: row.user,
                    number: row.number,
                    created_at: row.created_at,
                    current_price: calcStockPrice(row.is_special ? 'special' : 'normal', 1,row.is_complete),
                    ownership: row.ownership,
                }));
                setStocks(mapped);
                setTotalCount(res.data.total);
            })
            .catch((err) => console.error(err))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchStocks();
        scrollTop();
    }, [page, search]);

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        setPage(1);
    };

    return (
        <div>
            <Grid container justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Grid item size={{xs:12,md:6}}>
                    <RayaField
                        fullWidth
                        label={t('search')}
                        value={search}
                        onChange={handleSearchChange}
                        InputProps={{
                            endAdornment: (
                                <React.Fragment>
                                    <Search/>
                                </React.Fragment>
                            ),
                        }}
                    />
                </Grid>
            </Grid>

            {loading ? (
                <Grid container justifyContent="center" spacing={2}>
                    <ItemsSkeleton xs={12} sm={6} md={4} lg={4} length={12} height={150}/>
                </Grid>
            ) : (
                <Grid container spacing={2}>
                    {stocks.map((stock) => (
                        <Grid item size={{xs:12,sm:6,md:4}} key={stock.id}>
                            <Box className="bg-gray-box d-flex flex-column" style={{gap:5}}>
                                <Typography className="text-center font-14 bold card-title">{t('stockNumber')}: {stock.number}</Typography>
                                <Typography className="font-13 mt-10 bold" style={{color:theme.palette.secondary.main}}>
                                    {t('current_price')}: {price(stock.current_price)} {currencySymbol}
                                </Typography>
                                <Typography  className="font-13">
                                    {t('paid_time_price')}: {price(stock.ownership.price)} {currencySymbol}
                                    {!stock.ownership.is_cleared && ` (${t('hasInstallment')})`}
                                </Typography>
                                <Typography  className="font-13">
                                    {t('ownership_time')}: {showTableDate(stock.ownership.created_at)}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                    {
                        stocks.length === 0 &&
                        <Grid item size={{xs:12}}>
                            <NothingFound
                                type="page"
                                title={t('noStockFound')}
                                title_color="text"
                                //paragraph={t('noTicketFoundDesc')}
                                icon={<CircleOff/>}
                            >
                                <Button
                                    variant="outlined"
                                    startIcon={<ChevronLeft/>}
                                    component={Link}
                                    to={`/`}
                                >
                                    {t('back')}
                                </Button>
                            </NothingFound>
                        </Grid>
                    }
                </Grid>
            )}
            {
                Math.ceil(totalCount / perPage) > 1 &&
                <Grid container justifyContent="center" sx={{ mt: 4 }}>
                    <Pagination
                        count={Math.ceil(totalCount / perPage)}
                        page={page}
                        onChange={(e, value) => setPage(value)}
                        color="primary"
                    />
                </Grid>
            }

        </div>
    );
};

export default withStyles(Styles)(MyStocks);
